import React from 'react';

import {browserHistory, Link} from 'react-router';

import {
  LinkPagination,
  Props as LinkPaginationProps,
} from 'app/components/general/LinkPagination';
import {getFilterLink} from 'app/general/dashboards/DashboardFilterbar';
import {USE_STANDARD_PAGINATION_RANGE} from 'app/general/dates/date-range/utils';
import {getDueFilterIntervalData} from 'app/tasks/interval-filter';
import {StoredResponseType} from 'app/utils/redux_v2';

import type {DueFilterIntervalData} from 'app/tasks/interval-filter';
import type {TaskDashboardLocationParamsType} from 'app/tasks/types';
import type {Moment} from 'moment-tz';
import type {
  ListResponseType,
  SlimTaskInstanceSerializedType,
} from 'sweet-types';

interface Props
  extends Omit<
    LinkPaginationProps<SlimTaskInstanceSerializedType>,
    'response'
  > {
  params: TaskDashboardLocationParamsType;
  userTimezone: string;
  response: ListResponseType<SlimTaskInstanceSerializedType>;
}

export function TasksLinkPagination({
  params,
  userTimezone,
  response,
  ...other
}: Props): JSX.Element {
  const intervalData = getDueFilterIntervalData(params, userTimezone);

  const storedResponse = convertListResponseToStoredResponse(
    response,
    params.page ?? '1'
  );

  // when filtering by template id (No Date)
  if (
    params.template_id != null &&
    params.interval == null &&
    params.start == null &&
    params.end == null
  ) {
    return <LinkPagination response={storedResponse} {...other} />;
  }

  if (USE_STANDARD_PAGINATION_RANGE.includes(params.interval ?? '')) {
    return <LinkPagination response={storedResponse} {...other} />;
  }

  return (
    <div className="d-flex justify-content-center flex-wrap">
      {intervalData != null ? (
        <ul className="pagination mx-3">
          {renderLink(intervalData, 'subtract', 'fa-arrow-left', 'left')}
        </ul>
      ) : null}
      <LinkPagination response={storedResponse} page={params.page} {...other} />
      {intervalData != null ? (
        <ul className="pagination mx-3">
          {renderLink(intervalData, 'add', 'fa-arrow-right', 'right')}
        </ul>
      ) : null}
    </div>
  );
}

function getPageLink(start: Moment, end: Moment) {
  return getFilterLink(browserHistory, browserHistory.getCurrentLocation(), {
    start: start.toISOString(),
    end: end.toISOString(),
    interval: '',
  });
}

function renderLink(
  intervalData: DueFilterIntervalData,
  diffOperation: string,
  icon: string,
  iconPosition: string
): JSX.Element {
  const {start, end, count, unit, name} = intervalData;
  let diffUnit = unit.replace('isoweek', 'week');
  const destStart = start.clone()[diffOperation](count, diffUnit).startOf(unit);
  const destEnd = end.clone()[diffOperation](count, diffUnit).endOf(unit);
  const rangeName = name(destStart, destEnd);
  return (
    <li className="page-item">
      <Link className="page-link" to={getPageLink(destStart, destEnd)}>
        {iconPosition === 'left' ? <i className={`fa ${icon}`} /> : null}{' '}
        {rangeName}{' '}
        {iconPosition === 'right' ? <i className={`fa ${icon}`} /> : null}
      </Link>
    </li>
  );
}

export function convertListResponseToStoredResponse<T = any>(
  response: ListResponseType<T> | undefined,
  page: string | number = '1'
): StoredResponseType<T> {
  const intPage = parseInt(page as string);

  return response == null
    ? {
        loading: true,
        results: [],
        count: 1,
        next: null,
      }
    : {
        loading: false,
        results: response.results,
        count: response.count,
        next: response.next != null ? intPage + 1 : null,
        previous: response.previous != null ? intPage - 1 : null,
        numPages: response.num_pages,
      };
}
